import React from 'react'
import styled from 'styled-components'
import Modal, { CloseButton } from '@jarvis.oddle/core/components/Modal'
import { H4, P } from '@jarvis.oddle/core/components/Typo'
import Flex from '@jarvis.oddle/core/components/Flex'
import Form, { useFormContext } from '@jarvis.oddle/core/components/Form'
import Collapsible from '@jarvis.oddle/core/components/Collapsible'
import {
  colorConstants,
  margin,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import IconOddleEats from '@jarvis.oddle/core/components/Icons/IconOddleEats'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import IconVoucher from '@jarvis.oddle/core/components/Icons/IconVoucher'
import Loading from '@jarvis.oddle/core/components/Loading'
import showNotificationToast from '@jarvis.oddle/core/components/ToastNotification'

import VoucherItem from './VoucherItem'
import {
  fetchCheckoutMetaAction,
  getListOfVouchersAction,
  removeSelectedVoucherAction,
  selectCheckout,
  validateVoucherAction
} from 'src/redux/checkout'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import modalController, {
  makeControlledModal
} from 'src/decorators/ModalController'
import { isObjectEmpty } from 'src/utils/object'
import { capitalizeFirstLetter } from 'src/utils/string'
import useAppSelector from 'src/hooks/useAppSelector'

const RadioContainer = styled(Flex).attrs(() => ({
  paddingTop: paddingSizes.medium,
  marginTop: paddingSizes.small,
  marginBottom: paddingSizes.small,
  alignItems: 'center'
}))`
  border-radius: 6px;
  border: 1px
    ${(props) =>
      props.disabled || !props.checked
        ? colorConstants.lightGray
        : colorConstants.black}
    solid;
  ${(props) => (props.disabled ? '' : 'cursor: pointer;')}
`

const SubmitButton = () => {
  const { t } = React.useContext(TranslationContext)
  const { values } = useFormContext()
  return (
    <Form.SubmitButton
      fullWidth
      buttonColor={colorConstants.mainVoucherColor}
      buttonTextColor={colorConstants.white}
      disabled={!values.selectedVoucherID}
      buttonStyle='solid'
      buttonShape='round'
    >
      {({ loading, LoadingElement }) =>
        loading ? (
          LoadingElement
        ) : (
          <H4 bold normalised fontFamily='Roboto' color={colorConstants.white}>
            {t('Apply Voucher')}
          </H4>
        )
      }
    </Form.SubmitButton>
  )
}

const StyledModalHeader = styled(Modal.Header)`
  box-shadow: none;

  &&& {
    background-color: ${colorConstants.white};
  }
`

const StyledModalBody = styled(Modal.Body)`
  &&& {
    background-color: ${colorConstants.white};
  }
`

const StyledModalSection = styled(Modal.Section)`
  margin-top: ${margin[marginSizes.none]};
  height: 100%;
  padding: ${padding[paddingSizes.medium]};

  &&& {
    background-color: ${colorConstants.white};
  }
`

const StyledModalFooter = styled(Modal.Footer)`
  &&& {
    background-color: ${colorConstants.white};
  }
`

const StyledEmptyVoucher = styled(Flex)`
  text-align: center;
  display: flex;
  align-items: center;
  min-height: 400px;
  height: 100%;
`

const EmptyVoucherContent = React.memo(
  ({ gotError = false }: { gotError?: boolean }) => {
    const { t } = React.useContext(TranslationContext)
    return (
      <StyledEmptyVoucher justifyContent='center' flexDirection='column'>
        <IconVoucher width={140} height={140} />
        <H4 fontFamily='Roboto' color={colorConstants.matteBlack} bold>
          {t('No Oddle Eats vouchers available')}
        </H4>
        <P fontFamily='Roboto' color={colorConstants.matteBlack}>
          {gotError
            ? t('Cannot find available vouchers. Please try again later.')
            : t(
                'Available vouchers or vouchers issued to you will appear here.'
              )}
        </P>
      </StyledEmptyVoucher>
    )
  }
)

const VouchersSelectorModal = ({
  modal,
  setVoucherError = () => undefined
}: {
  modal: {
    show: boolean
    onClose: (params) => void
    onClosed: () => void
  }
  setVoucherError: (fieldName, messages) => void
}) => {
  const dispatch = useAppDispatch()
  const { t } = React.useContext(TranslationContext)
  const [showAvailableVouchers, setShowAvailableVouchers] =
    React.useState(false)
  const vouchers = useAppSelector(selectCheckout).vouchers
  const selectedVoucher = useAppSelector(selectCheckout).selectedVoucher
  const storeOrderId = useAppSelector(selectCheckout).meta.data?.storeOrder?.id
  const orderVouchers = useAppSelector(selectCheckout).meta.data?.storeOrder?.orderVouchers

  const selectedVoucherID = (selectedVoucher.data || {}).id

  const loadVouchers = async () => {
    await dispatch(getListOfVouchersAction())
    setShowAvailableVouchers(true)
  }

  const handleSubmit = React.useCallback(
    async (values, { setSubmitting }) => {
      setSubmitting(true)
      const voucherData = vouchers.data.find(
        (voucher) => voucher.id === values.selectedVoucherID
      )
      const resp = await dispatch(removeSelectedVoucherAction())
      if (resp?.payload && orderVouchers?.length > 0) {
        await dispatch(fetchCheckoutMetaAction({ storeOrderId }))
      }
      const { payload } = await dispatch(
        validateVoucherAction({ code: voucherData.code })
      )
      modalController.closeModal('VouchersSelectorModal')
      if (isObjectEmpty(payload?.data?.error)) {
        setVoucherError('voucher', [])
        showNotificationToast.success(t('Voucher Applied Successfully!'))
      } else {
        setVoucherError('voucher', [
          `${t('Selected voucher cannot be applied at this time.')} ${t(
            capitalizeFirstLetter(payload?.data?.error?.message || '')
          )}`
        ])
        showNotificationToast.error(t('Voucher cannot be applied at this time'))
      }
      setSubmitting(false)
    },
    [vouchers.data, dispatch, orderVouchers?.length, storeOrderId, setVoucherError, t]
  )

  const handleViewDetails = ({
    voucher,
    title,
    description,
    startDate,
    expirationDate,
    code,
    metadata
  }) => {
    modal.onClose(() => {
      modalController.showModal('VoucherDetailsModal', {
        voucher,
        title,
        description,
        startDate,
        expirationDate,
        code,
        metadata
      })
    })
  }

  return (
    <Modal {...modal} onEnter={loadVouchers} slideInAnimation='bottom'>
      <Form initialValues={{ selectedVoucherID }} onSubmit={handleSubmit}>
        <Modal.FlexContainer flexDirection='column'>
          <StyledModalHeader>
            <IconOddleEats width='120px' height='100%' />
            <CloseButton fill={colorConstants.black} onClick={modal.onClose} />
          </StyledModalHeader>
          <StyledModalBody>
            <StyledModalSection>
              {vouchers.status === 'pending' && (
                <StyledEmptyVoucher
                  justifyContent='center'
                  flexDirection='column'
                >
                  <Loading
                    width={80}
                    height={80}
                    color={colorConstants.mainVoucherColor}
                  />
                </StyledEmptyVoucher>
              )}
              {vouchers.status === 'rejected' && (
                <EmptyVoucherContent gotError />
              )}
              {vouchers.status === 'fulfilled' &&
                vouchers.data.length === 0 && <EmptyVoucherContent />}
              {vouchers.status === 'fulfilled' && vouchers.data.length > 0 && (
                <React.Fragment>
                  <Flex justifyContent='space-between'>
                    <H4
                      color={colorConstants.black}
                      margin={marginSizes.none}
                      fontFamily='Roboto'
                      bold
                    >
                      {t('Select a voucher to apply')}
                    </H4>
                  </Flex>
                  <Collapsible show={showAvailableVouchers}>
                    <Form.RadioGroup
                      name='selectedVoucherID'
                      resetOnUnmount={false}
                      selected={selectedVoucherID}
                    >
                      {vouchers.data.map((voucher) => (
                        <Form.Radio
                          key={voucher.id}
                          value={voucher.id}
                          Container={RadioContainer}
                          disabled={!voucher.isValid}
                        >
                          {({ checked }) => (
                            <VoucherItem
                              checked={checked}
                              voucher={voucher}
                              onClickDetails={handleViewDetails}
                            />
                          )}
                        </Form.Radio>
                      ))}
                    </Form.RadioGroup>
                  </Collapsible>
                </React.Fragment>
              )}
            </StyledModalSection>
          </StyledModalBody>
          {vouchers.status === 'fulfilled' && vouchers.data.length > 0 && (
            <StyledModalFooter>
              <SubmitButton />
            </StyledModalFooter>
          )}
        </Modal.FlexContainer>
      </Form>
    </Modal>
  )
}

export default makeControlledModal(
  VouchersSelectorModal,
  'VouchersSelectorModal'
)
